import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "./TiptapImage";
import Iframe from "./TiptapIframe";
import fileService from "../../../services/fileService";
import { REACT_APP_HOSTING_ENV } from "../../../config/env";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import InlineSection from "./TiptapInlineSection";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Link from "@tiptap/extension-link";
import MenuBar from "./TipTapEditorMenu";
import Highlight from "@tiptap/extension-highlight";
import "./tiptap-styles.scss";
import Messages from "../../../Shared/Messages";
import { themeVars } from "../../../utils/themeColor";
import ImageForHTMl from "./../ImageForHTMl";
import I18nProvider from "../../../services/I18nProvider";
import FontSize from "./TiptapFontSize";
import ModalPopup from "../../ModalPopup";
import MPSLink from "./TipTapMPSLink";
import TabHandler from "./TiptapTabHandler";
import MPSSelect from "../../MPSSelect";
import { diffWords } from "./transcriptDiff";
import { FormGroup, Label } from "reactstrap";
import MPSBarLoader from "../../MPSBarLoader";
import MPSButton from "../../Buttons/Buttons/MPSButton";
import Text from "../../Text";
import { API_ENDPOINTS } from "../../../config";
import { http } from "../../../utils/http";
import ContentLibrarySelector from "../../../Shared/Selectors/ContentLibrary/ContentLibrarySelector";
import SurveySelector from "../../../Shared/Selectors/Survey/SurveySelector";
import FeaturesService from "../../../services/featuresService";
import statusService from "../../../services/statusesService";
import aiAgentsService from "../../../services/aiAgentService";

const intl = I18nProvider.getIntlProvider();

// Wait after recording stops before tidying the transcript, so a quick
// stop/start does not spend a model call on text that is about to grow.
const AUTO_ENHANCE_DELAY_MS = 700;

// Track-changes colours for the read-only "Show changes" view. They never
// enter the note itself — that view is a temporary swap, restored on exit.
const DIFF_ADDED_COLOR = "#d2f4e8";
const DIFF_REMOVED_COLOR = "#fbdcdc";

const escapeHtml = (text) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");

export const docThemeVars = themeVars;

export default ({
  value,
  onChange,
  minimalEditor,
  hideMenuBar,
  patientTypeId,
  patientId,
  editorRef,
  templateParamType = "ALL",
  additionalExtensions = [],
}) => {
  const themeColor = useSelector((state) => {
    const raw = state.user?.data?.appTheme?.themeColor;
    return raw ? raw.replace("0x", "#") : "";
  });
  const initialLibValues = { libraryName: null, libraryId: null };
  const initialSurveyValues = { surveyName: null, surveyId: null };
  const [imageUploader, setImageUploader] = useState(false);
  const [files, setFiles] = useState([]);
  const [MPSLinkCreator, setMPSLinkCreator] = useState(false);
  const [MPSLinkType, setMPSLinkType] = useState(null);
  const [MPSLinkLayout, setMPSLinkLayout] = useState("anchor");
  const [MPSLinkAction, setMPSLinkAction] = useState("modal");
  const [surveyItem, setSurveyItem] = useState(initialSurveyValues);
  const [libraryItem, setLibraryItem] = useState(initialLibValues);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorContainerRef = useRef(null);

  const featureFlag = FeaturesService.isVoiceNoteDictationEnabled();

  const [newMention, setNewMention] = useState(false);
  const [templateParameters, setTemplateParameters] = useState([]);

  // --- Ambient recording state ---
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showEnhanceBar, setShowEnhanceBar] = useState(false);
  const [enhancedText, setEnhancedText] = useState("");
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceFailed, setEnhanceFailed] = useState(false);
  // What the editor shows while a review is open: the clean tidied text
  // (default), the tidied text with changes highlighted, or the raw dictation.
  const [reviewView, setReviewView] = useState("clean");
  const wasRecordingRef = useRef(false);
  const enhanceTimerRef = useRef(null);
  const lastEnhancedTranscriptRef = useRef("");
  // Bumped whenever the current review ends; an in-flight tidy from an older
  // session checks it and throws its result away.
  const enhanceSessionRef = useRef(0);
  // Doc snapshot taken when the raw-dictation view opens, so the tidied text
  // (with any edits) comes back intact when the view closes.
  const reviewDocHTMLRef = useRef("");
  const recognizerRef = useRef(null);
  const transcriptRef = useRef("");
  const preRecordingHTMLRef = useRef("");
  const preRecordingPosRef = useRef(0);
  const interimStartPosRef = useRef(null);
  const interimEndPosRef = useRef(null);

  // While the review card is open it is the only text box on screen.
  const reviewOpen = showEnhanceBar && !isRecording;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable the default image extension since we're using our custom one
        image: false,
      }),
      Image.configure({
        inline: true,
      }),
      Iframe,
      TextStyle,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Color,
      FontSize,
      InlineSection,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Subscript,
      Superscript,
      Link,
      MPSLink,
      Highlight.configure({ multicolor: true }),
      TabHandler,
      ...additionalExtensions,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync editor when parent value changes (e.g., template selected)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (typeof value === "string" && value !== current) {
      editor.commands.setContent(value, false); // don't emit update/focus
    }
  }, [editor, value]);

  const addImage = useCallback(
    (url, width, height) => {
      if (url) {
        editor.chain().focus().setImage({ src: url, width, height }).run();
      }
      setImageUploader(false);
    },
    [editor]
  );

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addNewFile = (file) => {
    files.push(file);
    setFiles([...files]);
  };

  const setImageWidth = (index, width) => {
    files[index].width = width;
    setFiles([...files]);
  };

  const setImageHeight = (index, height) => {
    files[index].height = height;
    setFiles([...files]);
  };

  const createMPSLink = () => {
    const baseClass = MPSLinkLayout === "anchor" ? "content-link" : "content-cta";
    let baseLink = "https://goto.mypatientspace.com/";
    let resourceName = "";
    let kindClass = "";

    if (MPSLinkType === "Content" && libraryItem.libraryName) {
      const encodedName = encodeURIComponent(libraryItem.libraryName);
      baseLink += `l?name=${encodedName}&env=${REACT_APP_HOSTING_ENV}`;
      resourceName = libraryItem.libraryName;
      kindClass = "library";
      setLibraryItem({ libraryName: null, libraryId: null });
    } else if (MPSLinkType === "Survey" && surveyItem.surveyName) {
      const encodedName = encodeURIComponent(surveyItem.surveyName);
      baseLink += `s?name=${encodedName}&env=${REACT_APP_HOSTING_ENV}`;
      resourceName = surveyItem.surveyName;
      kindClass = "survey";
      setSurveyItem({ surveyName: null, surveyId: null });
    } else {
      return;
    }

    // Use the highlighted text as the link label if any, otherwise the resource name
    const { from, to } = editor.state.selection;
    const selectedText = from !== to ? editor.state.doc.textBetween(from, to, " ") : "";
    const linkText = selectedText || resourceName;

    if (MPSLinkAction === "redirect") {
      baseLink += "&action=redirect";
    }

    const className = `${baseClass} ${kindClass}`;

    // Use the TipTap extension command if available, otherwise fall back to manual insertion
    if (editor.commands.insertMPSLink) {
      editor
        .chain()
        .focus()
        .insertMPSLink({
          type: MPSLinkType,
          name: linkText,
          url: baseLink,
          className,
        })
        .run();
    } else {
      // Fallback: Insert the link as HTML content directly
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${baseLink}" class="${className}">${linkText}</a>`
        )
        .run();
    }

    setMPSLinkCreator(false);
    setMPSLinkType(null);
    setMPSLinkLayout("anchor");
    setMPSLinkAction("modal");
  };

  const getTemplateParameters = async (type) => {
    let params = await statusService.loadTemplateParameters(type);
    let mappedParams = params.map((param) => {
      return { value: param.paramName, label: param.paramDescription };
    });
    mappedParams.push({ value: "_PatientLink", label: "Patient Link" });
    mappedParams.push({ value: "_TaskLink", label: "Task Link" });
    return mappedParams;
  };

  const mention = () => {
    setNewMention(true);
    getTemplateParameters(templateParamType).then((params) => {
      setTemplateParameters(params);
    });
  };

  const handleMentionSelect = (option) => {
    if (option) {
      editor.chain().focus().insertContent(option.value).run();
      setNewMention(false);
    }
  };

  // --- Ambient recording logic ---
  useEffect(() => {
    return () => {
      const rec = recognizerRef.current;
      if (!rec) return;
      try {
        if (rec.stopContinuousRecognitionAsync) rec.stopContinuousRecognitionAsync();
        else if (rec.stop) rec.stop();
      } catch (_) { }
      recognizerRef.current = null;
    };
  }, []);

  // Shared text-insertion helpers used by both Azure and browser engines
  const insertInterim = useCallback((text) => {
    const { state } = editor;
    if (interimStartPosRef.current === null) {
      interimStartPosRef.current = state.selection.from;
      interimEndPosRef.current = state.selection.from;
    }
    const from = interimStartPosRef.current;
    const to = interimEndPosRef.current;
    editor.view.dispatch(state.tr.insertText(text, from, to));
    interimEndPosRef.current = from + text.length;
  }, [editor]);

  const insertFinal = useCallback((text) => {
    transcriptRef.current += text + " ";
    const { state } = editor;
    const from = interimStartPosRef.current !== null
      ? interimStartPosRef.current
      : state.selection.from;
    const to = interimStartPosRef.current !== null
      ? interimEndPosRef.current
      : state.selection.from;
    editor.view.dispatch(state.tr.insertText(text + " ", from, to));
    interimStartPosRef.current = null;
    interimEndPosRef.current = null;
  }, [editor]);

  const startAzureRecording = useCallback(async () => {
    const SpeechSDK = window.SpeechSDK;
    if (!SpeechSDK) return false;

    try {
      const tokenResponse = await aiAgentsService.getSpeechToken();
      const { token, region } = tokenResponse;
      if (!token || !region) return false;

      const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(token, region);
      speechConfig.speechRecognitionLanguage = "en-US";
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

      recognizer.recognizing = (sender, event) => {
        if (event.result.text) insertInterim(event.result.text);
      };

      recognizer.recognized = (sender, event) => {
        if (
          event.result.reason === SpeechSDK.ResultReason.RecognizedSpeech &&
          event.result.text
        ) {
          insertFinal(event.result.text);
        }
      };

      recognizer.canceled = (sender, event) => {
        if (event.reason === SpeechSDK.CancellationReason.Error) {
          console.error("Speech recognition error:", event.errorDetails);
        }
        recognizer.stopContinuousRecognitionAsync();
        recognizerRef.current = null;
        setIsRecording(false);
        setIsInitializing(false);
        if (transcriptRef.current.trim()) setShowEnhanceBar(true);
      };

      recognizer.sessionStopped = () => {
        recognizerRef.current = null;
        setIsRecording(false);
      };

      return new Promise((resolve) => {
        recognizerRef.current = recognizer;
        recognizer.startContinuousRecognitionAsync(
          () => { setIsRecording(true); setIsInitializing(false); resolve(true); },
          (err) => { console.error("Azure start failed:", err); resolve(false); }
        );
      });
    } catch (err) {
      console.warn("Azure Speech unavailable, will use browser fallback:", err.message);
      return false;
    }
  }, [insertInterim, insertFinal]);

  const startBrowserRecording = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(intl.formatMessage(Messages.speechRecognitionNotSupported));
      setIsInitializing(false);
      return false;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      // One event can finalize a result and start the next interim at the
      // same time, so every result from resultIndex onward must be consumed.
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript.trim();
        if (!text) continue;
        if (result.isFinal) insertFinal(text);
        else interim += (interim ? " " : "") + text;
      }
      if (interim) insertInterim(interim);
    };

    recognition.onerror = (event) => {
      console.error("Browser speech error:", event.error);
      recognizerRef.current = null;
      setIsRecording(false);
      setIsInitializing(false);
      if (transcriptRef.current.trim()) setShowEnhanceBar(true);
    };

    recognition.onend = () => {
      // onend fires when .stop() is called — only update state if we're still tracking it
      if (recognizerRef.current === recognition) {
        recognizerRef.current = null;
        setIsRecording(false);
        if (transcriptRef.current.trim()) setShowEnhanceBar(true);
      }
    };

    recognizerRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    setIsInitializing(false);
    return true;
  }, [insertInterim, insertFinal]);

  const startRecording = useCallback(async (append = false) => {
    if (!editor) return;
    setIsInitializing(true);
    setShowEnhanceBar(false);
    // A previous enhancement is stale once more speech arrives. It is replaced
    // automatically when recording stops.
    setEnhancedText("");
    setEnhanceFailed(false);
    setReviewView("clean");
    enhanceSessionRef.current += 1;
    interimStartPosRef.current = null;
    if (!append) {
      // Fresh dictation: clear the buffer and anchor where the cursor is, so an
      // accepted enhancement can replace exactly the text this session dictated.
      transcriptRef.current = "";
      preRecordingHTMLRef.current = editor.getHTML();
      preRecordingPosRef.current = editor.state.selection.from;
    }
    // When appending, the buffer and the original anchor are kept, so the
    // enhancement still covers the whole dictation rather than the last part.

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        alert(intl.formatMessage(Messages.microphoneAccessRequired));
      } else {
        console.error("Mic access error:", err);
      }
      setIsInitializing(false);
      return;
    }

    const azureStarted = await startAzureRecording();
    if (!azureStarted) {
      startBrowserRecording();
    }
  }, [editor, startAzureRecording, startBrowserRecording]);

  const stopRecording = useCallback(() => {
    const rec = recognizerRef.current;
    if (!rec) return;
    if (rec.stopContinuousRecognitionAsync) {
      // Azure SDK recognizer
      rec.stopContinuousRecognitionAsync(
        () => {
          recognizerRef.current = null;
          setIsRecording(false);
          if (transcriptRef.current.trim()) setShowEnhanceBar(true);
        },
        (err) => {
          console.error("Azure stop error:", err);
          recognizerRef.current = null;
          setIsRecording(false);
        }
      );
    } else {
      // Browser Web Speech API — onend handler will fire setShowEnhanceBar
      rec.stop();
    }
  }, []);

  // Put the note back the way it was before this dictation started, and park
  // the cursor where the dictated text began.
  const restoreEditorToPreRecording = useCallback(() => {
    if (!editor) return;
    editor.commands.setContent(preRecordingHTMLRef.current, false);
    try {
      editor.commands.setTextSelection(preRecordingPosRef.current);
    } catch (_) {
      editor.commands.setTextSelection(editor.state.doc.content.size - 1);
    }
  }, [editor]);

  // Resume dictation and add to the existing transcript. The raw words go back
  // into the note first, so the new speech carries on from the end of them.
  const continueDictating = useCallback(() => {
    if (editor && transcriptRef.current.trim()) {
      editor.setEditable(true);
      restoreEditorToPreRecording();
      editor.chain().focus().insertContent(transcriptRef.current).run();
    }
    startRecording(true);
  }, [editor, restoreEditorToPreRecording, startRecording]);

  // The one record button covers both cases: with a review open it carries the
  // dictation on; otherwise it starts fresh.
  const toggleRecording = useCallback(() => {
    if (isRecording) stopRecording();
    else if (showEnhanceBar && transcriptRef.current.trim()) continueDictating();
    else startRecording(false);
  }, [
    isRecording,
    showEnhanceBar,
    continueDictating,
    startRecording,
    stopRecording,
  ]);

  const handleEnhance = useCallback(async () => {
    if (!transcriptRef.current.trim()) return;
    const session = enhanceSessionRef.current;
    lastEnhancedTranscriptRef.current = transcriptRef.current.trim();
    setIsEnhancing(true);
    setEnhanceFailed(false);
    try {
      const response = await aiAgentsService.enhanceTranscript(
        transcriptRef.current
      );
      let raw = response?.content || response?.response || response || "";
      if (typeof raw !== "string") raw = JSON.stringify(raw);
      let enhanced = raw;
      try {
        const parsed = JSON.parse(raw);
        enhanced = parsed?.response || parsed?.content || raw;
      } catch (_) { }
      if (session !== enhanceSessionRef.current) return;
      // Swap the raw words in the note for the clean tidied text. The clinician
      // reviews and edits right in the editor; the "Show changes" view is
      // rebuilt on demand from the stored texts.
      restoreEditorToPreRecording();
      editor.chain().insertContent(escapeHtml(enhanced)).run();
      setEnhancedText(enhanced);
    } catch (error) {
      console.error("Enhance failed:", error);
      if (session === enhanceSessionRef.current) setEnhanceFailed(true);
    }
    setIsEnhancing(false);
  }, [editor, restoreEditorToPreRecording]);

  // Tidying runs on its own. Whenever recording stops with speech captured,
  // enhance the whole transcript — including anything added by a later
  // "Continue Dictating" pass, so the preview always covers the full dictation.
  useEffect(() => {
    const justStopped = wasRecordingRef.current && !isRecording;
    wasRecordingRef.current = isRecording;
    if (!justStopped) return undefined;

    const transcript = transcriptRef.current.trim();
    if (!transcript) return undefined;

    // Nothing new since the last tidy — the note already shows the right text.
    if (transcript === lastEnhancedTranscriptRef.current) return undefined;

    // Short delay so a quick stop/start (or a mis-tap) does not spend a call.
    enhanceTimerRef.current = setTimeout(() => {
      enhanceTimerRef.current = null;
      handleEnhance();
    }, AUTO_ENHANCE_DELAY_MS);

    // Recording starting again cancels the pending tidy; the next stop covers
    // the fuller transcript instead.
    return () => {
      if (enhanceTimerRef.current) {
        clearTimeout(enhanceTimerRef.current);
        enhanceTimerRef.current = null;
      }
    };
  }, [isRecording, handleEnhance]);

  // Drop any pending tidy if the editor goes away mid-wait.
  useEffect(
    () => () => {
      if (enhanceTimerRef.current) clearTimeout(enhanceTimerRef.current);
    },
    []
  );

  // Switch what the editor shows during review. The clean view is the live,
  // editable note. The raw and track-changes views are read-only swaps: the
  // doc is snapshotted on the way out and restored — edits intact — on the
  // way back.
  const switchReviewView = useCallback(
    (next) => {
      if (!editor || next === reviewView) return;
      if (reviewView === "clean") {
        reviewDocHTMLRef.current = editor.getHTML();
      }
      if (next === "clean") {
        editor.commands.setContent(reviewDocHTMLRef.current, false);
        editor.setEditable(true);
      } else {
        restoreEditorToPreRecording();
        if (next === "original") {
          editor.commands.insertContent(
            escapeHtml(transcriptRef.current.trim())
          );
        } else {
          // Track changes: what was said struck out in red, what the tidy
          // step wrote in green.
          const { segments } = diffWords(
            transcriptRef.current.trim(),
            enhancedText
          );
          const html = segments
            .map((segment) => {
              const text = escapeHtml(segment.text);
              if (segment.removed) {
                return `<mark data-color="${DIFF_REMOVED_COLOR}"><s>${text}</s></mark>`;
              }
              if (segment.added) {
                return `<mark data-color="${DIFF_ADDED_COLOR}">${text}</mark>`;
              }
              return text;
            })
            .join("");
          editor.commands.insertContent(html);
        }
        editor.setEditable(false);
      }
      setReviewView(next);
    },
    [editor, reviewView, enhancedText, restoreEditorToPreRecording]
  );

  // Use this — keep whatever is on screen. In the clean view that is the
  // tidied text with any edits; in the raw view it is the words as dictated.
  // The track-changes view falls back to the clean text.
  const acceptEnhanced = useCallback(() => {
    if (!editor || !enhancedText) return;
    if (reviewView === "changes") {
      editor.commands.setContent(reviewDocHTMLRef.current, false);
    }
    editor.setEditable(true);
    editor.commands.focus();
    enhanceSessionRef.current += 1;
    setShowEnhanceBar(false);
    setEnhancedText("");
    setReviewView("clean");
    transcriptRef.current = "";
  }, [editor, enhancedText, reviewView]);

  // Keep my original — put the dictated words back into the note untouched.
  const dismissEnhance = useCallback(() => {
    enhanceSessionRef.current += 1;
    if (editor && transcriptRef.current.trim()) {
      editor.setEditable(true);
      restoreEditorToPreRecording();
      editor.chain().focus().insertContent(transcriptRef.current.trim()).run();
    }
    setShowEnhanceBar(false);
    setEnhancedText("");
    setReviewView("clean");
    transcriptRef.current = "";
  }, [editor, restoreEditorToPreRecording]);

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only deactivate if leaving the editor container entirely
    if (!editorContainerRef.current?.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        handleImageUpload(file);
      }
    }
  };

  // Clipboard paste handler
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          handleImageUpload(file);
        }
        break;
      }
    }
  }, []);

  const handleImageUpload = async (file) => {
    try {
      setUploading(true);

      // Validate file
      if (!file.type.startsWith("image/")) {
        console.error("File is not an image");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        console.error("File is too large (max 10MB)");
        return;
      }

      // Upload file
      const contents = await fileService.uploadFiles("IMAGE", file, [], true);

      if (contents && contents[0]) {
        // Insert image at current cursor position
        editor
          .chain()
          .focus()
          .setImage({
            src: contents[0].contentURL,
            width: 400,
            height: null,
          })
          .run();
      }
    } catch (error) {
      console.error("Image upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const fetchAll = async () => {
    setLoading(false);
  };

  useEffect(() => {
    if (editorRef) {
      editorRef.current = {
        editor,
      };
    }
    fetchAll();

    // Add paste event listener to editor
    const editorElement =
      editorContainerRef.current?.querySelector(".ProseMirror");
    if (editorElement) {
      editorElement.addEventListener("paste", handlePaste);
      return () => {
        editorElement.removeEventListener("paste", handlePaste);
      };
    }
  }, [editor, editorRef, handlePaste]);

  return (
    <div>
      {!hideMenuBar && (
        <>
          <MenuBar
            editor={editor}
            addImage={() => setImageUploader(true)}
            createMPSLink={() => setMPSLinkCreator(true)}
            setLink={setLink}
            minimalEditor={minimalEditor}
            mention={mention}
            isRecording={isRecording}
            isInitializing={isInitializing}
            onToggleRecording={featureFlag ? toggleRecording : undefined}
          />
          <hr style={{ borderTop: "2px solid rgba(0, 0, 0, 0.1" }} />
        </>
      )}

      {/* Dictation review bar. The tidied text sits in the editor itself with
          the changes highlighted; this bar only carries the controls. */}
      {reviewOpen && (
        <div className="dictation-review-bar">
          {isEnhancing || (!enhancedText && !enhanceFailed) ? (
            <span className="dictation-review-bar__busy">
              <i className="fa fa-spinner fa-spin mr-1" />
              <Text content={Messages.enhancingTranscript} />
            </span>
          ) : enhanceFailed && !enhancedText ? (
            <>
              <span className="dictation-review-bar__failed">
                <Text content={Messages.enhanceFailed} />
              </span>
              <MPSButton size="sm" color="primary" onClick={handleEnhance}>
                <i className="fas fa-sync-alt mr-1" />
                <Text content={Messages.retry} />
              </MPSButton>
              <MPSButton size="sm" type="block" onClick={dismissEnhance}>
                <Text content={Messages.keepMyOriginal} />
              </MPSButton>
            </>
          ) : (
            <>
              <div className="dictation-review-bar__header">
                <span className="dictation-review-bar__title">
                  <i className="fas fa-magic dictation-review-bar__title-icon" />
                  <Text content={Messages.cleanedUpDictation} />
                </span>
                <span className="dictation-review-bar__spacer" />
                <MPSButton
                  size="sm"
                  type="block"
                  onClick={() =>
                    switchReviewView(
                      reviewView === "changes" ? "clean" : "changes"
                    )
                  }
                >
                  <Text
                    content={
                      reviewView === "changes"
                        ? Messages.hideChanges
                        : Messages.showChanges
                    }
                  />
                </MPSButton>
                <MPSButton
                  size="sm"
                  type="block"
                  onClick={() =>
                    switchReviewView(
                      reviewView === "original" ? "clean" : "original"
                    )
                  }
                >
                  <Text
                    content={
                      reviewView === "original"
                        ? Messages.hideWhatISaid
                        : Messages.showWhatISaid
                    }
                  />
                </MPSButton>
              </div>
            </>
          )}
        </div>
      )}

      {newMention && (
        <MPSSelect
          options={templateParameters}
          placeholder="Mention"
          onChange={handleMentionSelect}
        />
      )}

      <div
        ref={editorContainerRef}
        className={`editor-container ${dragActive ? "drag-active" : ""} ${uploading ? "uploading" : ""
          }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          position: "relative",
          backgroundColor: "#fafafa",
          borderRadius: 4,
          transition: "all 0.3s ease",
          ...(themeColor ? { "--mps-content-button-color": themeColor } : {}),
          ...docThemeVars(themeColor),
        }}
      >
        {dragActive && (
          <div className="drag-overlay">
            <div className="drag-message">
              <i className="fa fa-cloud-upload-alt fa-3x mb-3" />
              <div className="h5">Drop image here to upload</div>
              <small>Images will be automatically uploaded and inserted</small>
            </div>
          </div>
        )}

        {uploading && (
          <div className="upload-overlay">
            <div className="upload-message">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="sr-only">Uploading...</span>
              </div>
              <div>Uploading image...</div>
            </div>
          </div>
        )}

        <EditorContent
          editor={editor}
          style={{
            padding: 5,
          }}
        />
      </div>

      {/* Confirm button sits under the note while a review is open. It takes
          whichever view is showing, so there is no separate keep-original. */}
      {reviewOpen && enhancedText && !isEnhancing && (
        <div className="dictation-review-actions">
          <MPSButton size="sm" color="primary" onClick={acceptEnhanced}>
            <Text content={Messages.useThisText} />
          </MPSButton>
        </div>
      )}

      <style>{`
        .editor-container {
          min-height: 100px;
        }

        .editor-container.drag-active {
          border: 2px dashed #007bff !important;
          background-color: #e3f2fd !important;
        }

        .drag-overlay,
        .upload-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          border-radius: 4px;
        }

        .drag-message,
        .upload-message {
          text-align: center;
          color: #007bff;
          padding: 2rem;
        }

        .drag-message i {
          color: #007bff;
        }

        .editor-container .ProseMirror {
          min-height: 75px;
          background-color: transparent;
        }

        .editor-container.uploading .ProseMirror {
          pointer-events: none;
        }
      `}</style>
      {imageUploader && (
        <ModalPopup
          title={intl.formatMessage(Messages.file_upload)}
          onClose={() => setImageUploader(false)}
        >
          <ImageForHTMl
            addFile={addNewFile}
            existingFiles={files}
            selectImage={addImage}
            setImageWidth={setImageWidth}
            setImageHeight={setImageHeight}
          />
        </ModalPopup>
      )}
      {MPSLinkCreator && (
        <ModalPopup onClose={() => setMPSLinkCreator(false)}>
          <FormGroup>
            <Label for="linkType">Link Type</Label>
            <MPSSelect
              options={[
                { value: "Content", label: "Content" },
                { value: "Survey", label: "Survey" },
              ]}
              value={MPSLinkType}
              onChange={(e) => setMPSLinkType(e?.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="linkLayout">Layout</Label>
            <MPSSelect
              options={[
                { value: "button", label: "Button" },
                { value: "anchor", label: "Anchor" },
              ]}
              value={MPSLinkLayout}
              onChange={(e) => setMPSLinkLayout(e?.value || "button")}
              clearable={false}
            />
          </FormGroup>
          <FormGroup>
            <Label for="linkAction">On Click</Label>
            <MPSSelect
              options={[
                { value: "modal", label: "Open in modal" },
                { value: "redirect", label: "Redirect to page" },
              ]}
              value={MPSLinkAction}
              onChange={(e) => setMPSLinkAction(e?.value || "modal")}
              clearable={false}
            />
          </FormGroup>
          {loading && MPSLinkType !== null && (
            <div>
              <span>Loading titles</span>
              <MPSBarLoader>Loading Content</MPSBarLoader>
            </div>
          )}

          {MPSLinkType &&
            !loading &&
            (MPSLinkType === "Content" ? (
              <ContentLibrarySelector
                patientTypeId={patientTypeId}
                saveLibraryName={(libraryName, libraryId) => {
                  setLibraryItem({
                    libraryName: libraryName,
                    libraryId: libraryId,
                  });
                }}
                libraryName={libraryItem.libraryName}
                libraryId={libraryItem.libraryId}
              />
            ) : (
              <SurveySelector
                save={(surveyId, surveyTitle, surveyName) => {
                  setSurveyItem({ surveyName: surveyName, surveyId: surveyId });
                }}
                patientTypeId={patientTypeId}
                surveyId={surveyItem.surveyId}
                surveyName={surveyItem.surveyName}
                noCreate
              />
            ))}
          {((MPSLinkType === "Content" && libraryItem.libraryName) ||
            (MPSLinkType === "Survey" && surveyItem.surveyName)) && (
            <div
              style={{
                marginTop: 20,
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <MPSButton onClick={() => createMPSLink()}>Save</MPSButton>
            </div>
          )}
        </ModalPopup>
      )}
    </div>
  );
};
